import type { SkillModel } from '@/typings/models/Skill';

/**
 * Keeps typed slugs close to what the API accepts (lowercase words joined by
 * dashes or underscores); anything else it still rejects with a message.
 */
export function normalizeSlugInput(value?: string): string {
  return (value || '').toLowerCase().replace(/\s+/g, '-');
}

/** Skills in alphabetical order of their label, whatever order they arrived in. */
export function sortByLabel<T extends Pick<SkillModel, 'label'>>(skills?: T[] | null): T[] {
  return [...(skills || [])].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

/** Selected slugs in the alphabetical order of their skills, not the order they were picked in. */
export function sortSlugsByLabel(slugs: string[], skills: Pick<SkillModel, 'label' | 'slug'>[]): string[] {
  const labels = new Map(skills.map((skill) => [skill.slug, skill.label]));
  const labelOf = (slug: string) => labels.get(slug) ?? slug;
  return [...slugs].sort((a, b) => labelOf(a).localeCompare(labelOf(b), undefined, { sensitivity: 'base' }));
}

/** Select options for picking skills; the API takes skills by slug. */
export function skillOptions(skills: Pick<SkillModel, 'label' | 'slug'>[]): { value: string; label: string }[] {
  return skills.map((skill) => ({ value: skill.slug, label: skill.label }));
}

/** A user's skills as the slugs the skills select holds. */
export function skillSlugs(skills?: Pick<SkillModel, 'slug'>[] | null): string[] {
  return (skills || []).map((skill) => skill.slug);
}

/** Whether a skill save failed on the slug (taken, or not in the allowed format). */
export function isSlugError(errorMessage?: string): boolean {
  return /\bslug\b/i.test(errorMessage || '');
}
